import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { SelectOption } from '../../../interfaces/common';
import { RPMFilters } from '../common/RPMFilters';
import {
  FilterConfiguration,
  Filters,
  LoadingStates,
} from '../types/rpms.types';
import {
  createClinicFilterFetcher,
  fetchInsuranceCompanyOptions,
  fetchCptCodeOptions,
  fetchMemberOptions,
  fetchProcessIdOptions,
  fetchClaimStatusOptions,
} from '../utils/filterHelpers';

interface RPMClaimsFiltersProps {
  clinicOptions: SelectOption[];
  filters: Filters;
  onApplyFilters: (filters: Filters) => void;
  onClearFilters: () => void;
  loadingStates: LoadingStates;
  selectedCount: number;
  additionalButtons?: React.ReactNode;
}

export const RPMClaimsFilters: React.FC<RPMClaimsFiltersProps> = ({
  clinicOptions,
  filters,
  onApplyFilters,
  onClearFilters,
  loadingStates,
  selectedCount,
  additionalButtons,
}) => {
  const filterConfigurations: FilterConfiguration<Filters>[] = useMemo(
    () => [
      // Row 1: Date of Service, Claim Created From, Claim Created To
      {
        key: 'dateOfService',
        label: 'Date Of Service',
        type: 'date',
        options: [],
        section: 'RPM Claims',
      },
      {
        key: 'invoiceCreationDateFrom',
        label: 'Claim Created From',
        type: 'date',
        options: [],
        section: 'RPM Claims',
      },
      {
        key: 'invoiceCreationDateTo',
        label: 'Claim Created To',
        type: 'date',
        options: [],
        section: 'RPM Claims',
      },
      // Row 2: Clinic, Insurance Company, CPT Codes
      {
        key: 'clinic',
        label: 'Clinic',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        fetchOptions: createClinicFilterFetcher(clinicOptions),
      },
      {
        key: 'insuranceCompany',
        label: 'Insurance Company',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        fetchOptions: fetchInsuranceCompanyOptions,
      },
      {
        key: 'cptCodes',
        label: 'CPT Codes',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        fetchOptions: fetchCptCodeOptions,
      },
      // Row 3: Member Search, Process IDs, Claim Status
      {
        key: 'memberUuids',
        label: 'Member Search',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        minSearchLength: 2,
        placeholder: 'Type at least 2 characters to search for members...',
        fetchOptions: fetchMemberOptions,
      },
      {
        key: 'processIds',
        label: 'Process IDs',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        defaultSearchTerm: '',
        placeholder: 'Search process IDs...',
        fetchOptions: fetchProcessIdOptions,
      },
      {
        key: 'statuses',
        label: 'Claim Status',
        options: [],
        section: 'RPM Claims',
        type: 'autocomplete',
        multiple: true,
        fetchOptions: fetchClaimStatusOptions,
      },
    ],
    [clinicOptions],
  );

  return (
    <Box sx={{ mb: 3 }}>
      <RPMFilters
        configurations={filterConfigurations}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        initialFilters={filters}
        filters={filters}
        selectedCount={selectedCount}
        loadingStates={loadingStates}
        additionalButtons={additionalButtons}
      />
    </Box>
  );
};